/**
 * Factory for providers that are registered in CastingManager but are NOT
 * operational from the web SPA until a native TV/receiver app (or native
 * shell) exists.
 *
 * Critical: never invent devices, never report available/connected, never
 * claim play works. Exposes explicit CastingCapabilityState only.
 */

import { createEmitter } from "../../Utilities/events";
import {
  CastingCapabilityState,
  CastingConnectionState,
} from "./CastingEvents";
import {
  createCastReceiverSession,
  createReceiverControlMessage,
  normalizeWhiteLabelCastConfig,
} from "./CastReceiverContract";

/**
 * @param {object} spec
 * @param {string} spec.id provider id (roku|firetv|samsung|lg|future_native)
 * @param {string} spec.displayName
 * @param {string} spec.capabilityState requires_receiver_app | requires_native_app | unsupported
 * @param {string} [spec.reason] human-readable honest status
 * @param {object} [spec.receiverContract] documented contract surface
 * @param {object} [options] white-label / feature options
 */
export function createReceiverRequiredProvider(spec, options) {
  spec = spec || {};
  options = options || {};
  var emitter = createEmitter();
  var whiteLabel = normalizeWhiteLabelCastConfig(options.whiteLabel || options);
  var capabilityState =
    spec.capabilityState || CastingCapabilityState.REQUIRES_RECEIVER_APP;
  var connectionState = CastingConnectionState.DISCONNECTED;
  var lastError = null;
  var sessionDraft = null;

  function capabilities() {
    return {
      id: spec.id,
      name: spec.displayName || spec.id,
      discover: false,
      play: false,
      pause: false,
      seek: false,
      stop: false,
      volume: false,
      mute: false,
      playbackRate: false,
      textTracks: false,
      audioTracks: false,
      reconnect: false,
      operationalOnWeb: false,
      requiresReceiverApp:
        capabilityState === CastingCapabilityState.REQUIRES_RECEIVER_APP,
      requiresNativeApp:
        capabilityState === CastingCapabilityState.REQUIRES_NATIVE_APP,
      reason:
        spec.reason ||
        (spec.displayName || spec.id) +
          " requires a branded receiver / native app before cast is operational.",
      receiverContract: spec.receiverContract || null,
      whiteLabel: {
        applicationKey: whiteLabel.applicationKey,
        applicationName: whiteLabel.applicationName,
        logo: whiteLabel.logo,
        receiverApplicationId: whiteLabel.receiverApplicationId,
        defaultReceiver: whiteLabel.defaultReceiver,
        supportedCastProviders: whiteLabel.supportedCastProviders,
      },
    };
  }

  function rejectOp(code, message) {
    lastError = { code: code, message: message, recoverable: false };
    emitter.emit("error", lastError);
    return Promise.reject(new Error(message));
  }

  return {
    name: spec.id,
    getCapabilities: capabilities,
    getCapabilityState: function () {
      return capabilityState;
    },
    /**
     * Draft a CastReceiverSession for future native/TV apps — does not connect.
     */
    buildReceiverSession: function (partial) {
      sessionDraft = createCastReceiverSession(
        Object.assign({}, partial || {}, {
          provider: spec.id,
          applicationKey: whiteLabel.applicationKey,
          applicationName: whiteLabel.applicationName,
          logo: whiteLabel.logo,
          receiverApplicationId: whiteLabel.receiverApplicationId,
        })
      );
      return sessionDraft;
    },
    /**
     * Documented control message builder for the future receiver bridge.
     */
    buildControlMessage: function (action, extra) {
      var session =
        sessionDraft ||
        createCastReceiverSession({
          provider: spec.id,
          applicationKey: whiteLabel.applicationKey,
          applicationName: whiteLabel.applicationName,
        });
      return createReceiverControlMessage(action, session, extra);
    },
    initialize: function () {
      // Honest: not available for web discovery / connect
      emitter.emit("availability", false);
      emitter.emit("capability", capabilityState);
      emitter.emit("devices", []);
      return Promise.resolve();
    },
    isAvailable: function () {
      return false;
    },
    isConnected: function () {
      return false;
    },
    discoverDevices: function () {
      // Never invent LAN devices for receiver-required platforms
      return Promise.resolve([]);
    },
    getDevices: function () {
      return [];
    },
    connect: function () {
      connectionState = CastingConnectionState.ERROR;
      return rejectOp(
        "REQUIRES_RECEIVER",
        (spec.displayName || spec.id) +
          " cast is not operational from this browser until a native receiver app is installed and paired."
      ).catch(function (err) {
        connectionState = CastingConnectionState.DISCONNECTED;
        return Promise.reject(err);
      });
    },
    disconnect: function () {
      connectionState = CastingConnectionState.DISCONNECTED;
      return Promise.resolve();
    },
    loadMedia: function () {
      return rejectOp(
        "REQUIRES_RECEIVER",
        "Cannot load media: " +
          (spec.displayName || spec.id) +
          " receiver app required"
      );
    },
    play: function () {
      return rejectOp("REQUIRES_RECEIVER", "Play not available without receiver");
    },
    pause: function () {
      return rejectOp("REQUIRES_RECEIVER", "Pause not available without receiver");
    },
    seek: function () {
      return rejectOp("REQUIRES_RECEIVER", "Seek not available without receiver");
    },
    stop: function () {
      return Promise.resolve();
    },
    setVolume: function () {
      return rejectOp(
        "REQUIRES_RECEIVER",
        "Volume not available without receiver"
      );
    },
    setMuted: function () {
      return rejectOp("REQUIRES_RECEIVER", "Mute not available without receiver");
    },
    synchronizeState: function () {
      return Promise.resolve({
        target: "local",
        playing: false,
        currentTime: 0,
        capabilityState: capabilityState,
      });
    },
    getRemoteState: function () {
      return {
        target: "local",
        playing: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        muted: false,
        playbackRate: 1,
        capabilityState: capabilityState,
      };
    },
    getConnectionState: function () {
      return connectionState;
    },
    getLastError: function () {
      return lastError;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      emitter.removeAllListeners();
    },
  };
}

export default createReceiverRequiredProvider;
